import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/auth-client';

/**
 * F3.3 — Route guard for protected pages.
 *
 * Wraps a route element and redirects unauthenticated users to /login,
 * carrying the original path as `?next=` so the LoginPage can return
 * them after a successful sign-in.
 *
 * Use:
 *   <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
 *
 * While session is loading, renders nothing (avoids a flash of the
 * unauthenticated redirect for already-logged-in users).
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  if (isPending) return null;
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search + location.hash);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
